import { useState, useCallback, useRef } from 'react';

/**
 * AdventureImageGallery - Horizontally scrolling adventure images
 *
 * Displays route/adventure images in a slow auto-scrolling gallery.
 * Used on the landing page between hero text and query input.
 *
 * Features:
 * - Slow infinite horizontal scroll animation
 * - Pause on hover
 * - Click to fill query input
 * - Seamless loop (images duplicated)
 * - Navigation arrows for manual scrolling
 * - Responsive card sizing
 */

export interface AdventureRoute {
  id: string;
  name: string;
  imageUrl: string;
  exampleQuery: string;
}

interface AdventureImageGalleryProps {
  /** Routes to display in the gallery */
  routes?: AdventureRoute[];
  /** Callback when a route card is clicked */
  onRouteClick: (query: string) => void;
  /** Scroll animation duration in seconds (default: 30) */
  scrollDuration?: number;
}

/**
 * Default routes with VERIFIED location-accurate Unsplash images
 * Each image has been manually verified to show the actual trail/location
 *
 * Image sources (all free Unsplash License):
 * - West Highland Way: Buachaille Etive Beag, Glencoe by Cat Bassano
 * - Cape Wrath Trail: Ben Wyvis, Scottish Highlands by Ross Sneddon
 * - Lake District: Haystacks, Buttermere by Josh Kirk
 * - Coast to Coast: Yorkshire Dales sheep & dry stone walls by Donna Elliot
 * - Pennine Way: Peak District moorland by Nik Demidko
 * - South Downs Way: Seven Sisters chalk cliffs by Joseph Pearson
 */
const DEFAULT_ROUTES: AdventureRoute[] = [
  {
    id: 'west-highland-way',
    name: 'West Highland Way',
    // VERIFIED: Hikers on Buachaille Etive Beag, Glencoe, Scotland
    imageUrl: 'https://images.unsplash.com/photo-1556008132-75d56bdf1f39?w=400&h=300&fit=crop',
    exampleQuery: '5 days on the West Highland Way in June',
  },
  {
    id: 'cape-wrath-trail',
    name: 'Cape Wrath Trail',
    // VERIFIED: Ben Wyvis, remote Scottish Highlands wilderness
    imageUrl: 'https://images.unsplash.com/photo-1631531756906-f256d7c94447?w=400&h=300&fit=crop',
    exampleQuery: 'Cape Wrath Trail in September, ultralight',
  },
  {
    id: 'lake-district',
    name: 'Lake District',
    // VERIFIED: Haystacks, Buttermere, Lake District
    imageUrl: 'https://images.unsplash.com/photo-1628616043557-78460cc89ab7?w=400&h=300&fit=crop',
    exampleQuery: 'Weekend in the Lake District, budget kit',
  },
  {
    id: 'coast-to-coast',
    name: 'Coast to Coast',
    // VERIFIED: Yorkshire Dales with sheep and dry stone walls
    imageUrl: 'https://images.unsplash.com/photo-1669263042215-fb021e8aae1e?w=400&h=300&fit=crop',
    exampleQuery: 'Coast to Coast using B&Bs, group of 4',
  },
  {
    id: 'pennine-way',
    name: 'Pennine Way',
    // VERIFIED: Peak District moorland hiking
    imageUrl: 'https://images.unsplash.com/photo-1620127471607-eb3d1ebfc8f0?w=400&h=300&fit=crop',
    exampleQuery: '2 weeks on the Pennine Way in July',
  },
  {
    id: 'south-downs-way',
    name: 'South Downs Way',
    // VERIFIED: Seven Sisters chalk cliffs, Eastbourne, UK
    imageUrl: 'https://images.unsplash.com/photo-1500027359505-e004de7ed703?w=400&h=300&fit=crop',
    exampleQuery: 'South Downs Way long weekend',
  },
];

export function AdventureImageGallery({
  routes = DEFAULT_ROUTES,
  onRouteClick,
  scrollDuration = 30,
}: AdventureImageGalleryProps) {
  const [isPaused, setIsPaused] = useState(false);
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleRouteClick = useCallback(
    (route: AdventureRoute) => {
      onRouteClick(route.exampleQuery);
    },
    [onRouteClick]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, route: AdventureRoute) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleRouteClick(route);
      }
    },
    [handleRouteClick]
  );

  const handleImageError = useCallback((routeId: string) => {
    setImageErrors((prev) => new Set(prev).add(routeId));
  }, []);

  // Scroll by one card width
  const scrollByAmount = useCallback((direction: 'left' | 'right') => {
    if (!scrollRef.current) return;

    // Pause animation when manually scrolling
    setIsPaused(true);

    const cardWidth = 320; // Approximate card width + gap
    const scrollAmount = direction === 'left' ? -cardWidth : cardWidth;

    // Get current transform value
    const currentTransform = scrollRef.current.style.transform;
    const match = currentTransform.match(/translateX\((-?\d+(?:\.\d+)?)px\)/);
    const currentX = match ? parseFloat(match[1]) : 0;

    // Apply new transform with transition
    scrollRef.current.style.transition = 'transform 0.3s ease-out';
    scrollRef.current.style.transform = `translateX(${currentX - scrollAmount}px)`;

    // Reset transition after animation
    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.style.transition = '';
      }
    }, 300);
  }, []);

  // Don't render if no routes
  if (!routes || routes.length === 0) {
    return null;
  }

  // Duplicate routes for seamless infinite loop
  const displayRoutes = [...routes, ...routes];

  return (
    <div
      data-testid="adventure-gallery"
      role="region"
      aria-label="Featured adventure routes"
      className="relative w-full overflow-x-hidden py-8"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Left Navigation Arrow */}
      <button
        data-testid="gallery-arrow-left"
        onClick={() => scrollByAmount('left')}
        aria-label="Scroll left"
        className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors backdrop-blur-sm border border-white/20 focus:outline-none focus:ring-2 focus:ring-orange-500"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      {/* Right Navigation Arrow */}
      <button
        data-testid="gallery-arrow-right"
        onClick={() => scrollByAmount('right')}
        aria-label="Scroll right"
        className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors backdrop-blur-sm border border-white/20 focus:outline-none focus:ring-2 focus:ring-orange-500"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Gradient fade edges */}
      <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-neutral-950 to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-neutral-950 to-transparent z-10 pointer-events-none" />

      <div
        ref={scrollRef}
        data-testid="adventure-gallery-scroll"
        className={`flex gap-4 px-4 ${isPaused ? 'paused' : ''}`}
        style={{
          animationName: isPaused ? 'none' : 'scroll-left',
          animationDuration: `${scrollDuration}s`,
          animationTimingFunction: 'linear',
          animationIterationCount: 'infinite',
          animationPlayState: isPaused ? 'paused' : 'running',
        }}
      >
        {displayRoutes.map((route, index) => {
          const hasError = imageErrors.has(route.id);
          const uniqueKey = `${route.id}-${index}`;

          return (
            <button
              key={uniqueKey}
              onClick={() => handleRouteClick(route)}
              onKeyDown={(e) => handleKeyDown(e, route)}
              aria-label={`Plan a trip: ${route.exampleQuery}`}
              className="relative shrink-0 w-[280px] h-[180px] sm:w-[320px] sm:h-[200px] rounded-lg overflow-hidden group cursor-pointer transition-transform duration-300 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 focus:ring-offset-neutral-950"
            >
              {/* Image or Gradient Fallback */}
              {!hasError ? (
                <img
                  src={route.imageUrl}
                  alt={`${route.name} landscape`}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  onError={() => handleImageError(route.id)}
                />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-orange-600 via-amber-700 to-neutral-900" />
              )}

              {/* Gradient Overlay */}
              <div
                data-testid="route-card-overlay"
                className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"
              />

              {/* Route Name */}
              <div className="absolute inset-0 flex flex-col justify-end p-4">
                <h3 className="font-mono font-bold text-lg text-white drop-shadow-lg">
                  {route.name}
                </h3>
                <p className="text-sm text-white/70 mt-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 line-clamp-2">
                  {route.exampleQuery}
                </p>
              </div>
            </button>
          );
        })}
      </div>

      {/* CSS Animation Keyframes */}
      <style>{`
        @keyframes scroll-left {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
      `}</style>
    </div>
  );
}
